import {
  Box,
  ConfirmationDialogBox,
  Grid,
  Skeleton,
  Typography,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { Button, ToastType } from '@components';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import {
  addFlagCommentV4,
  deleteFlagComment,
  getFlagComment,
} from '@store/orders';
import { DeleteIcon } from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { convertUtcToLocalAndFormat } from '@utils/dateFormat';
import { handleToast } from '@utils/utils';
import { DELETE_BILLING_NOTE_COMMENT_DIALOG_ID } from '@views/order/constants';
import { isEmpty } from 'lodash';
import { Component, Show, createSignal, onMount } from 'solid-js';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

import { editBillingModalClasses as cls } from './classes';

type BillNoteFlagCommentsProps = {
  flagId: number;
};

const BillNoteFlagComments: Component<BillNoteFlagCommentsProps> = (
  props: BillNoteFlagCommentsProps,
) => {
  const flagId = props.flagId;
  const [comment, setComment] = createSignal('');
  const [comments, setComments] = createSignal<
    Record<string, string | number>[]
  >([]);
  const [loading, setLoading] = createSignal({
    fetch: false,
    add: false,
  });
  const [deleteCommentId, setDeleteCommentId] = createSignal<number | null>(
    null,
  );

  const addCommentOnBillingNote = async (
    e: Event & {
      submitter: HTMLElement | null;
    } & {
      currentTarget: HTMLFormElement;
      target: Element;
    },
  ) => {
    e.preventDefault();
    try {
      setLoading({
        ...loading(),
        add: true,
      });
      const response = await addFlagCommentV4(
        flagId,
        comment(),
        CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderFlagAddComment,
      );
      setComments([
        response as unknown as Record<string, string | number>,
        ...comments(),
      ]);
      handleToast(ToastType.Success, 'Comment Added Successfully');
      setComment('');
    } catch (error) {
      handleToast(ToastType.Error, 'Failed to Add Comment');
    } finally {
      setLoading({
        ...loading(),
        add: false,
      });
    }
  };

  const getCommentOnBillingNote = async () => {
    try {
      setLoading({
        ...loading(),
        fetch: true,
      });
      const response = await getFlagComment(flagId);
      setComments(response);
    } catch (error) {
      handleToast(ToastType.Error, 'Failed to Fetch Comment');
    } finally {
      setLoading({
        ...loading(),
        fetch: false,
      });
    }
  };

  const deleteCommentOnBillingNote = async () => {
    try {
      if (deleteCommentId() === null) return;
      const response = await deleteFlagComment(
        deleteCommentId()!,
        CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderFlagDeleteComment,
      );
      if (!isEmpty(response) && Boolean(response.isSuccess)) {
        const newComments = comments().filter(
          (x) => x.id !== deleteCommentId(),
        );
        setComments(newComments);
        handleToast(ToastType.Success, 'Comment Deleted Successfully');
      }
    } catch (error) {
      handleToast(ToastType.Error, 'Failed to Delete Comment');
    } finally {
      setDeleteCommentId(null);
    }
  };

  onMount(() => getCommentOnBillingNote());

  return (
    <>
      <Grid item size={12}>
        <Typography
          variant="body1"
          component="p"
          class={cls.messageHistoryTitle}
        >
          Message History
        </Typography>
      </Grid>
      <Grid item size={12} class="flex flex-col gap-2">
        <form onSubmit={addCommentOnBillingNote}>
          <TextField
            label="Add a Comment"
            placeholder="Enter description here"
            value={comment()}
            onChange={(value: string) => setComment(value)}
            dataTestId="add-a-comment-textInput"
          />
          <Button
            type="submit"
            label="Add Comment"
            class="hidden!"
            dataTestId={formatDataTestId('Add Comment', 'button')}
          />
        </form>
      </Grid>
      <Show when={loading().fetch}>
        <Grid item size={12}>
          <Skeleton variant="rectangular" class="mb-2" />
          <Skeleton variant="rectangular" class="mb-2" />
          <Skeleton variant="rectangular" class="mb-2" />
          <Skeleton variant="rectangular" class="mb-2" />
        </Grid>
      </Show>
      {comments()
        .toReversed()
        .map((comment: Record<string, string | number>) => (
          <Grid item size={12}>
            <Box class={cls.messageHeader}>
              <Typography
                variant="h4"
                component="h4"
                class={cls.messageUserName}
              >
                {comment.name}
              </Typography>
              <Box class={cls.messageHeaderRight}>
                <Typography
                  variant="body2"
                  component="span"
                  class={cls.messageDateTime}
                >
                  {convertUtcToLocalAndFormat(
                    comment.timestamp as string,
                    'MMMM dd, yyyy h:mm a',
                  )}
                </Typography>
                <DeleteIcon
                  class={cls.deleteIcon}
                  onClick={() => {
                    setDeleteCommentId(Number(comment.id));
                    openDialogBox(DELETE_BILLING_NOTE_COMMENT_DIALOG_ID);
                  }}
                />
              </Box>
            </Box>
            <Box class="p-2">{comment.comment}</Box>
          </Grid>
        ))}
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(
          DELETE_BILLING_NOTE_COMMENT_DIALOG_ID,
        )}
        title={'Are you sure you want to delete this comment?'}
        dataTestId={DELETE_BILLING_NOTE_COMMENT_DIALOG_ID}
        id={DELETE_BILLING_NOTE_COMMENT_DIALOG_ID}
        onSubmit={deleteCommentOnBillingNote}
        onSubmitText="Ok"
        closeDialogBox={(_reason) => {
          setDeleteCommentId(null);
          closeDialogBox(DELETE_BILLING_NOTE_COMMENT_DIALOG_ID);
        }}
      />
    </>
  );
};

export default BillNoteFlagComments;
